import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import { BACKEND_URL } from "src/api/endpoints";
import {
  Configuration,
  DesiredSpec,
  LifecycleState,
  ModuleAttributes,
  ModuleCreateRequest,
  ModuleKind,
  ModuleListResponse,
  ModuleOperationCreateRequest,
  ModuleOperationGetResponse,
  ModuleOperationListResponse,
  ModuleOperationPatchRequest,
  ModuleOperationResourceData,
  ModuleOperationStatus,
  ModuleOperationsApi,
  ModuleResourceData,
  ModulesApi,
  Spec,
} from "src/clients/flow-api";

// --- Client setup ---

const config = new Configuration({ basePath: BACKEND_URL });
const modulesApi = new ModulesApi(config);
const moduleOperationsApi = new ModuleOperationsApi(config);

const DEFAULT_STALE_TIME = 1000 * 60 * 5; // 5 minutes

// --- Query keys ---

export const moduleKeys = {
  all: () => ["modules"] as const,
  list: (filters?: {
    workspaceId?: string;
    kind?: ModuleKind;
    include?: string;
    pageLimit?: number;
    includeInactive?: boolean;
  }) => ["modules", "list", filters] as const,
  detail: (moduleId: string, include?: string) =>
    ["modules", "detail", moduleId, include] as const,
};

export const moduleOperationKeys = {
  all: () => ["moduleOperations"] as const,
  list: (
    moduleId: string,
    filters?: { include?: string; pageOffset?: number; pageLimit?: number },
  ) => ["moduleOperations", "list", moduleId, filters] as const,
  detail: (operationId: string, filters?: { include?: string }) =>
    ["moduleOperations", "detail", operationId, filters] as const,
};

// --- Domain types (transformed from JSON:API envelopes) ---

export type ModuleSpec = {
  kind: string;
  version: string;
  is_active: boolean;
  profile_level: number;
  lifecycle_state?: LifecycleState;
  config: Record<string, unknown>;
};

export type ModuleOperation = {
  id: string;
  status: ModuleOperationStatus;
  patch: Record<string, unknown>;
  tags: Record<string, unknown> | null;
  createdAt: string | null;
  updatedAt: string | null;
};

export type Module = {
  id: string;
  workspaceId: string;
  kind: string;
  spec: ModuleSpec | null;
  desiredSpec: ModuleSpec;
  activeOperations: ModuleOperation[];
  latestResolvedOperation: ModuleOperation | null;
};

// --- Transformation helpers ---

const transformSpec = (
  spec: Spec | DesiredSpec | undefined | null,
): ModuleSpec | null => {
  if (!spec) return null;
  return {
    kind: String(spec.kind ?? ""),
    version: String(spec.version ?? ""),
    is_active: Boolean(spec.is_active),
    profile_level: Number(spec.profile_level ?? 0),
    lifecycle_state: spec.lifecycle_state,
    config: (spec.config as Record<string, unknown>) ?? {},
  };
};

export const transformOperation = (
  op: ModuleOperationResourceData,
): ModuleOperation => ({
  id: String(op.id),
  status: op.attributes.status,
  patch: (op.attributes.patch as Record<string, unknown>) ?? {},
  tags: (op.attributes.tags as Record<string, unknown>) ?? null,
  createdAt: (op.attributes as Record<string, unknown>).created_at
    ? String((op.attributes as Record<string, unknown>).created_at)
    : null,
  updatedAt: (op.attributes as Record<string, unknown>).updated_at
    ? String((op.attributes as Record<string, unknown>).updated_at)
    : null,
});

const buildIncludedOpsMap = (
  included: ModuleOperationResourceData[] | undefined,
): Map<string, ModuleOperationResourceData> => {
  const map = new Map<string, ModuleOperationResourceData>();
  if (!included) return map;
  for (const item of included) {
    if (item.type === "module_operations") {
      map.set(String(item.id), item);
    }
  }
  return map;
};

const extractRelationshipIds = (
  relationships: ModuleResourceData["relationships"],
  relName: string,
): string[] => {
  const rel = relationships?.[relName];
  if (!rel?.data) return [];
  const data = Array.isArray(rel.data) ? rel.data : [rel.data];
  return data.map((d: { id?: string }) => String(d.id ?? "")).filter(Boolean);
};

const transformModuleResource = (
  resource: ModuleResourceData,
  opsMap: Map<string, ModuleOperationResourceData>,
): Module => {
  const attrs: ModuleAttributes = resource.attributes;
  const rels = resource.relationships;

  const workspaceRelData = rels?.workspace?.data;
  const workspaceId = workspaceRelData
    ? String(
        (Array.isArray(workspaceRelData)
          ? workspaceRelData[0]?.id
          : workspaceRelData.id) ?? "",
      )
    : "";

  const activeOpIds = extractRelationshipIds(rels, "active_operations");
  const latestResolvedOpIds = extractRelationshipIds(
    rels,
    "latest_resolved_operation",
  );

  const activeOperations = activeOpIds
    .map((id) => opsMap.get(id))
    .filter((op): op is ModuleOperationResourceData => op !== undefined)
    .map(transformOperation);

  const latestResolvedOp =
    latestResolvedOpIds
      .map((id) => opsMap.get(id))
      .filter((op): op is ModuleOperationResourceData => op !== undefined)
      .map(transformOperation)[0] ?? null;

  return {
    id: String(resource.id),
    workspaceId,
    kind: String((attrs.desired_spec as Record<string, unknown>)?.kind ?? ""),
    spec: transformSpec(attrs.spec as Spec | undefined),
    desiredSpec: transformSpec(attrs.desired_spec as DesiredSpec) as ModuleSpec,
    activeOperations,
    latestResolvedOperation: latestResolvedOp,
  };
};

export const transformModuleListResponse = (
  response: ModuleListResponse,
): Module[] => {
  const dataArray: ModuleResourceData[] = Array.isArray(response.data)
    ? response.data
    : [response.data];
  const opsMap = buildIncludedOpsMap(response.included);
  return dataArray.map((resource) => transformModuleResource(resource, opsMap));
};

// --- Module hooks ---

type UseModulesArgs = {
  workspaceId?: string;
  kind?: ModuleKind;
  include?: string;
  pageLimit?: number;
};

export const useModules = ({
  workspaceId,
  kind,
  include,
  pageLimit,
}: UseModulesArgs) =>
  useQuery<Module[], AxiosError>({
    queryKey: moduleKeys.list({ workspaceId, kind, include, pageLimit }),
    queryFn: async () => {
      const response = await modulesApi.listModulesApiV1ModulesGet(
        workspaceId,
        kind,
        undefined,
        include,
        undefined,
        pageLimit,
      );
      return transformModuleListResponse(response.data);
    },
    enabled: workspaceId !== undefined ? !!workspaceId : true,
    staleTime: DEFAULT_STALE_TIME,
  });

export const useModulesByKind = () => {
  const include = "active_operations,latest_resolved_operation";
  const allKinds = Object.values(ModuleKind);

  // Always fetch inactive modules too; the admin panel hides them client-side behind a
  // "Show inactive modules" toggle, so a single fetch serves both toggle states.
  return useQueries({
    queries: allKinds.map((kind) => ({
      queryKey: moduleKeys.list({
        kind,
        include,
        pageLimit: 1000,
        includeInactive: true,
      }),
      queryFn: async () => {
        const response = await modulesApi.listModulesApiV1ModulesGet(
          undefined,
          kind,
          true,
          include,
          undefined,
          1000,
        );
        return transformModuleListResponse(response.data);
      },
      staleTime: DEFAULT_STALE_TIME,
    })),
    combine: (results) => {
      const byKind = Object.fromEntries(
        allKinds.map((kind, i) => [kind, results[i].data ?? []]),
      ) as Record<ModuleKind, Module[]>;

      const isLoading = results.some((r) => r.isLoading);
      const isError = results.some((r) => r.isError);

      const allModules = allKinds.flatMap((kind) => byKind[kind]);

      return { byKind, allModules, isLoading, isError };
    },
  });
};

// Narrow fetch used by the Model Quotas admin tab
export const useAgentsModulesForQuotas = () =>
  useQuery<Module[], AxiosError>({
    queryKey: moduleKeys.list({
      kind: ModuleKind.AGENTS,
      pageLimit: 1000,
      includeInactive: true,
    }),
    queryFn: async () => {
      const response = await modulesApi.listModulesApiV1ModulesGet(
        undefined,
        ModuleKind.AGENTS,
        true,
        undefined,
        undefined,
        1000,
      );
      return transformModuleListResponse(response.data);
    },
    staleTime: DEFAULT_STALE_TIME,
  });

export const useModuleDetail = (moduleId: string | null) => {
  const include = "all_operations";
  return useQuery<Module, Error>({
    queryKey: moduleKeys.detail(moduleId ?? "", include),
    queryFn: async () => {
      if (!moduleId) {
        throw new Error("Unexpected missing moduleId");
      }
      const response = await modulesApi.getModuleApiV1ModulesModuleIdGet(
        moduleId,
        include,
      );
      const data = response.data;
      const opsMap = buildIncludedOpsMap(data.included);
      return transformModuleResource(data.data, opsMap);
    },
    enabled: !!moduleId,
    staleTime: DEFAULT_STALE_TIME,
  });
};

export const useCreateModule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: ModuleCreateRequest) => {
      const response = await modulesApi.createModuleApiV1ModulesPost(request);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: moduleKeys.all() });
    },
  });
};

// --- Operation hooks ---

export const useListModuleOperations = (
  moduleId?: string,
  options?: {
    include?: string;
    pageOffset?: number;
    pageLimit?: number;
  },
) => {
  return useQuery<ModuleOperationListResponse, Error>({
    queryKey: moduleOperationKeys.list(moduleId ?? "", options),
    queryFn: async () => {
      if (!moduleId) {
        throw new Error("Unexpected missing moduleId");
      }
      const response =
        await moduleOperationsApi.listModuleOperationsApiV1ModuleOperationsGet(
          moduleId,
          options?.include,
          options?.pageOffset,
          options?.pageLimit,
        );
      return response.data;
    },
    enabled: !!moduleId,
    staleTime: DEFAULT_STALE_TIME,
  });
};

export const useGetModuleOperation = (
  operationId?: string,
  options?: {
    include?: string;
  },
) => {
  return useQuery<ModuleOperationGetResponse, Error>({
    queryKey: moduleOperationKeys.detail(operationId ?? "", options),
    queryFn: async () => {
      if (!operationId) {
        throw new Error("Unexpected missing operationId");
      }
      const response =
        await moduleOperationsApi.getModuleOperationApiV1ModuleOperationsOperationIdGet(
          operationId,
          options?.include,
        );
      return response.data;
    },
    enabled: !!operationId,
    staleTime: DEFAULT_STALE_TIME,
  });
};

// --- Single-module refresh --------------------------------------------------

// Re-fetches one module and patches it into every list cache that contains it.
export const useRefreshModule = () => {
  const queryClient = useQueryClient();
  const include = "active_operations,latest_resolved_operation";

  return async (moduleId: string) => {
    const response = await modulesApi.getModuleApiV1ModulesModuleIdGet(
      moduleId,
      include,
    );
    const data = response.data;
    const opsMap = buildIncludedOpsMap(data.included);
    const freshModule = transformModuleResource(data.data, opsMap);

    queryClient.setQueriesData<Module[]>(
      { queryKey: ["modules", "list"] },
      (old) => old?.map((m) => (m.id === moduleId ? freshModule : m)),
    );

    return freshModule;
  };
};

export const useCreateOperation = () => {
  const queryClient = useQueryClient();
  const refreshModule = useRefreshModule();

  return useMutation({
    mutationFn: async (request: ModuleOperationCreateRequest) => {
      const response =
        await moduleOperationsApi.createModuleOperationApiV1ModuleOperationsPost(
          request,
        );
      return response.data;
    },
    onSuccess: async (_data, variables) => {
      const moduleId = variables?.data?.relationships?.module?.data?.id;
      if (moduleId) {
        try {
          await refreshModule(moduleId);
        } catch {
          queryClient.invalidateQueries({ queryKey: moduleKeys.all() });
        }
      } else {
        queryClient.invalidateQueries({ queryKey: moduleKeys.all() });
      }
      queryClient.invalidateQueries({
        queryKey: moduleOperationKeys.all(),
      });
    },
    retry: 1,
  });
};

export const useUpdateOperation = () => {
  const queryClient = useQueryClient();
  const refreshModule = useRefreshModule();

  return useMutation({
    mutationFn: async ({
      operationId,
      moduleId,
      request,
    }: {
      operationId: string;
      moduleId?: string;
      request: ModuleOperationPatchRequest;
    }) => {
      const response =
        await moduleOperationsApi.updateModuleOperationApiV1ModuleOperationsOperationIdPatch(
          operationId,
          request,
        );
      return { data: response.data, moduleId };
    },
    onSuccess: async (result) => {
      if (result.moduleId) {
        try {
          await refreshModule(result.moduleId);
        } catch {
          queryClient.invalidateQueries({ queryKey: moduleKeys.all() });
        }
      } else {
        queryClient.invalidateQueries({ queryKey: moduleKeys.all() });
      }
      queryClient.invalidateQueries({
        queryKey: moduleOperationKeys.all(),
      });
    },
  });
};

// --- Infinite operations hook -----------------------------------------------

const INFINITE_PAGE_SIZE = 20;

export const useInfiniteModuleOperations = (
  moduleId: string,
  options?: { enabled?: boolean },
) => {
  return useInfiniteQuery({
    queryKey: moduleOperationKeys.list(moduleId, {
      include: "infinite",
    }),
    queryFn: async ({
      pageParam = 0,
    }): Promise<ModuleOperationListResponse> => {
      const response =
        await moduleOperationsApi.listModuleOperationsApiV1ModuleOperationsGet(
          moduleId,
          undefined,
          pageParam as number,
          INFINITE_PAGE_SIZE,
        );
      return response.data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.links?.next
        ? (lastPageParam as number) + INFINITE_PAGE_SIZE
        : undefined,
    enabled: options?.enabled ?? true,
    staleTime: 0,
  });
};
